// src/chatbot/components/SuggestedItems.tsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaStar,
  FaLeaf,
  FaSeedling,
  FaShoppingCart,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { FaWheatAwn } from "react-icons/fa6";
import type { MenuItem, Branch } from "~/types/new_types";
import {
  getItemName,
  getItemDescription,
  itemIs,
} from "~/utils/menuItemHelper";
import { useBranch } from "~/contexts/BranchContext";
import { useShoppingCart } from "~/contexts/ShoppingCartContext";

interface SuggestedItemsProps {
  items: MenuItem[];
  theme: Branch["theme"];
  onItemClick?: (item: MenuItem) => void;
}

const SuggestedItems: React.FC<SuggestedItemsProps> = ({
  items,
  theme,
  onItemClick,
}) => {
  const { t, i18n } = useTranslation();
  const { branch, getConvertedPrice } = useBranch();
  const { setTempQuantity, getTempQuantity } = useShoppingCart();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 25,
      },
    },
  };

  // Cart functions for individual items
  const handleFirstAdd = (item: MenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    setTempQuantity(item.id, 1);
  };

  const handleQuantityIncrease = (item: MenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    const currentQuantity = getTempQuantity(item.id);
    const newQuantity = currentQuantity + 1;
    setTempQuantity(item.id, newQuantity);
  };

  const handleQuantityDecrease = (item: MenuItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.is_available) return;
    const currentQuantity = getTempQuantity(item.id);
    const newQuantity = Math.max(0, currentQuantity - 1);
    setTempQuantity(item.id, newQuantity);
  };

  if (items.length === 0) return null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-2"
    >
      <div
        className="text-xs font-medium mb-2"
        style={{ color: `${theme.textColor}80` }}
      >
        {t("chatbot.suggestedItems")}:
      </div>

      <div className="space-y-2">
        {items.map((item) => {
          const currentQuantity = getTempQuantity(item.id);
          const displayPrice = getConvertedPrice(item.price);
          const displayDiscountedPrice = item.discounted_price
            ? getConvertedPrice(item.discounted_price)
            : null;

          return (
            <motion.div
              key={item.id}
              variants={itemVariants}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onItemClick?.(item)}
              className="flex gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200"
              style={{
                backgroundColor: `${theme.primaryColor}08`,
                border:
                  theme.cardStyle === "bordered"
                    ? `1px solid ${theme.borderColor}30`
                    : "none",
                borderRadius: theme.borderRadius,
              }}
            >
              {/* Item Image */}
              <div
                className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0"
                style={{ borderRadius: theme.borderRadius }}
              >
                <img
                  src={
                    item.image_url ??
                    branch?.default_item_image ??
                    "/images/no-image-available.jpg"
                  }
                  alt={getItemName(item, i18n.language)}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/images/broken-image.jpg";
                  }}
                />
              </div>

              {/* Item Details */}
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4
                      className="font-medium text-sm truncate"
                      style={{ color: theme.textColor }}
                    >
                      {getItemName(item, i18n.language)}
                    </h4>

                    {getItemDescription(item, i18n.language) && (
                      <p
                        className="text-xs line-clamp-1 mt-1"
                        style={{ color: `${theme.textColor}70` }}
                      >
                        {getItemDescription(item, i18n.language)}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div
                    className="text-sm font-bold flex-shrink-0"
                    style={{ color: theme.primaryColor }}
                  >
                    {item.discounted_price ? (
                      <div className="flex flex-col items-end">
                        <span
                          className="text-xs line-through"
                          style={{ color: `${theme.textColor}60` }}
                        >
                          {displayPrice}
                        </span>
                        <span>{displayDiscountedPrice}</span>
                      </div>
                    ) : (
                      <span>{displayPrice}</span>
                    )}
                  </div>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-1">
                  {item.is_featured && (
                    <div
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                      style={{
                        backgroundColor: `${theme.accentColor}20`,
                        color: theme.accentColor,
                      }}
                    >
                      <FaStar size={8} />
                      <span>{t("menu.popular")}</span>
                    </div>
                  )}

                  {itemIs(item, "vegetarian") && (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: `${theme.accentColor}20`,
                        color: theme.accentColor,
                      }}
                      title={t("menu.vegetarian")}
                    >
                      <FaLeaf size={8} />
                    </div>
                  )}

                  {itemIs(item, "vegan") && (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: `${theme.accentColor}30`,
                        color: theme.accentColor,
                      }}
                      title={t("menu.vegan")}
                    >
                      <FaSeedling size={8} />
                    </div>
                  )}

                  {itemIs(item, "gluten-free") && (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: `${theme.accentColor}20`,
                        color: theme.accentColor,
                      }}
                      title={t("menu.glutenFree")}
                    >
                      <FaWheatAwn size={8} />
                    </div>
                  )}

                  {!item.is_available && (
                    <div
                      className="px-2 py-0.5 rounded-full text-xs"
                      style={{
                        backgroundColor: `${theme.secondaryColor}20`,
                        color: theme.secondaryColor,
                      }}
                    >
                      {t("menuItem.unavailable")}
                    </div>
                  )}
                </div>

                {/* Add to cart functionality */}
                {/* <div className="mt-2">
                  {currentQuantity === 0 ? (
                    <motion.button
                      onClick={(e) => handleFirstAdd(item, e)}
                      className="w-full py-1.5 rounded-lg flex items-center justify-center gap-1 text-white font-medium text-xs"
                      whileHover={{ scale: item.is_available ? 1.02 : 1 }}
                      whileTap={{ scale: item.is_available ? 0.98 : 1 }}
                      style={{
                        backgroundColor: item.is_available
                          ? theme.primaryColor
                          : `${theme.primaryColor}50`,
                        borderRadius: theme.borderRadius,
                        boxShadow: item.is_available
                          ? `0 2px 8px -1px ${theme.primaryColor}30`
                          : "none",
                      }}
                      disabled={!item.is_available}
                    >
                      <FaShoppingCart size={10} />
                      <span>
                        {item.is_available
                          ? t("menu.addToOrder")
                          : t("menu.unavailable")}
                      </span>
                    </motion.button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <motion.button
                        onClick={(e) => handleQuantityDecrease(item, e)}
                        className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-white font-medium"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          backgroundColor: theme.primaryColor,
                          borderRadius: theme.borderRadius,
                          boxShadow: `0 2px 6px -1px ${theme.primaryColor}30`,
                        }}
                      >
                        <FaMinus size={8} />
                      </motion.button>

                      <div
                        className="flex-1 py-1 text-center font-medium text-xs"
                        style={{
                          color: theme.primaryColor,
                          borderRadius: theme.borderRadius,
                        }}
                      >
                        {currentQuantity}
                      </div>

                      <motion.button
                        onClick={(e) => handleQuantityIncrease(item, e)}
                        className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-white font-medium"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          backgroundColor: theme.primaryColor,
                          borderRadius: theme.borderRadius,
                          boxShadow: `0 2px 6px -1px ${theme.primaryColor}30`,
                        }}
                      >
                        <FaPlus size={8} />
                      </motion.button>
                    </div>
                  )}
                </div> */}
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default SuggestedItems;
